import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { getPatientEncounterLabel } from "./patientEncounterLabel";

interface UseTestReportsParams {
  searchCode?: string;
}

const useTestReports = (params?: UseTestReportsParams) => {
  let searchCode = params?.searchCode?.trim().toUpperCase();

  // Format code: if more than 3 chars, ensure 4th char is '.'
  if (searchCode && searchCode.length > 3 && searchCode[3] !== ".") {
    searchCode = `${searchCode.slice(0, 3)}.${searchCode.slice(3)}`;
  }

  const queryResult = useQuery({
    queryKey: ["medical-text-examples", searchCode],
    queryFn: async () => {
      // If there's a search code, we need to find medical_text_examples that have
      // matching codes in procedures, diagnoses, or co-morbidities
      if (searchCode) {
        // Get medical text IDs with matching ICD-10 codes (diagnoses)
        const { data: diagnosisMatches, error: diagnosisError } = await supabase
          .from("expected_diagnosis")
          .select(
            `
            medical_text_example_id,
            icd10_codes!inner(code)
          `,
          )
          .ilike("icd10_codes.code", searchCode);

        if (diagnosisError) throw diagnosisError;

        // Get medical text IDs with matching ICD-10 codes (co-morbidities)
        const { data: coMorbidityMatches, error: coMorbidityError } = await supabase
          .from("expected_co_morbidities")
          .select(
            `
            medical_text_example_id,
            icd10_codes!inner(code)
          `,
          )
          .ilike("icd10_codes.code", searchCode);

        if (coMorbidityError) throw coMorbidityError;

        // Get medical text IDs with matching OPCS-4 codes (procedures)
        const { data: procedureMatches, error: procedureError } = await supabase
          .from("expected_procedures")
          .select(
            `
            medical_text_example_id,
            opcs4_codes!inner(code)
          `,
          )
          .ilike("opcs4_codes.code", searchCode);

        if (procedureError) throw procedureError;

        // Combine all matching medical text IDs
        const matchingIds = new Set<string>();
        diagnosisMatches?.forEach((match) => {
          if (match.medical_text_example_id) {
            matchingIds.add(match.medical_text_example_id);
          }
        });
        coMorbidityMatches?.forEach((match) => {
          if (match.medical_text_example_id) {
            matchingIds.add(match.medical_text_example_id);
          }
        });
        procedureMatches?.forEach((match) => {
          if (match.medical_text_example_id) {
            matchingIds.add(match.medical_text_example_id);
          }
        });

        // If no matches found, return empty array
        if (matchingIds.size === 0) {
          return [];
        }

        // Fetch the medical text examples for matching IDs
        const { data, error } = await supabase
          .from("medical_text_examples")
          .select(
            `
          *,
          patient_medical_text_examples(
            patient_id,
            patients!inner(
              id
            )
          ),
          patient_encounter_medical_text_examples(
            patient_encounter_id,
            patient_encounters!inner(
              id,
              admit_datetime
            )
          )
        `,
          )
          .in("id", Array.from(matchingIds))
          .order("sequence_number", { ascending: true });

        if (error) throw error;

        const mappedData = data.map((example) => ({
          ...example,
          patient_info: example.patient_medical_text_examples
            ? {
                patient_id: example.patient_medical_text_examples.patient_id,
                id: example.patient_medical_text_examples.patients.id,
              }
            : null,
          encounter_info: example.patient_encounter_medical_text_examples
            ? {
                encounter_id: example.patient_encounter_medical_text_examples.patient_encounter_id,
                integration_encounter_id: getPatientEncounterLabel(
                  example.patient_encounter_medical_text_examples.patient_encounters,
                ),
              }
            : null,
        }));

        return mappedData;
      }

      // No search - return all examples
      const { data, error } = await supabase
        .from("medical_text_examples")
        .select(
          `
          *,
          patient_medical_text_examples(
            patient_id,
            patients!inner(
              id
            )
          ),
          patient_encounter_medical_text_examples(
            patient_encounter_id,
            patient_encounters!inner(
              id,
              admit_datetime
            )
          )
        `,
        )
        .order("sequence_number", { ascending: true });

      if (error) throw error;

      const mappedData = data.map((example) => ({
        ...example,
        patient_info: example.patient_medical_text_examples
          ? {
              patient_id: example.patient_medical_text_examples.patient_id,
              id: example.patient_medical_text_examples.patients.id,
            }
          : null,
        encounter_info: example.patient_encounter_medical_text_examples
          ? {
              encounter_id: example.patient_encounter_medical_text_examples.patient_encounter_id,
              integration_encounter_id: getPatientEncounterLabel(
                example.patient_encounter_medical_text_examples.patient_encounters,
              ),
            }
          : null,
      }));

      return mappedData;
    },
  });

  return queryResult;
};

export default useTestReports;
